import {
  Box,
  ButtonProps,
  Drawer,
  IconButton,
  InputBase,
  Paper,
  styled,
  SwipeableDrawer,
  Tooltip,
} from "@mui/material";
import {
  Filter,
  OutputDTOQueryFilterTemplateArray,
  QueryFilterTemplate,
  SortDirection,
} from "../../services/swagger";
import {
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react";
import { AxiosPromise } from "axios";
import FilterBox from "./FilterBox";
import { prepareFilterString } from "../../helpers/filters";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import useRefresh from "../../hooks/useRefresh";
import useDidMountEffect from "../../hooks/useDidMountEffect";
import { useSearchParams } from "react-router-dom";
import {
  filtersToParamString,
  paramStringToObject,
} from "../../helpers/params";
import useBreakpoint from "../../hooks/useBreakpoint";
import DataTable, { DataTableHeader } from "./DataTable";
import CardList from "./CardList";
import { useTranslation } from "react-i18next";
import SearchIcon from "@mui/icons-material/Search";
import AddButton from "../atoms/AddButton";

export interface QueryParameters {
  search: string;
  filters: string;
  skip: number;
  limit: number;
  sortField?: string;
  sortDirection?: SortDirection;
}

interface DataViewProps<T> {
  name: string;
  button?: Omit<ButtonProps, "sx"> & { text: string };
  headers: DataTableHeader[];
  items: T[];
  count: number;
  pagination?: boolean;
  renderItem: (item: T) => ReactNode;
  queryFunction?: () => AxiosPromise<OutputDTOQueryFilterTemplateArray>;
  onChange?: (query: QueryParameters) => any;
  onItemClick?: (item: T) => any;
  disableFiltersOpenByDefault?: boolean;
  loading: boolean;
}

const drawerWidth = 300;

const MainContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "open",
})<{
  open?: boolean;
  mobile?: boolean;
}>(({ theme, mobile }) => ({
  display: "flex",
  flexDirection: "column",
  flexGrow: 1,
  width: `calc(100% - ${drawerWidth * 2}px)`,
  ...(mobile
    ? {}
    : {
        transition: theme.transitions.create("margin", {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
        marginRight: -drawerWidth,
        position: "relative",
        variants: [
          {
            props: ({ open }) => open,
            style: {
              transition: theme.transitions.create("margin", {
                easing: theme.transitions.easing.easeOut,
                duration: theme.transitions.duration.enteringScreen,
              }),
              marginRight: theme.shape.borderRadius as number,
            },
          },
        ],
      }),
}));

export default function DataView<T>(props: DataViewProps<T>) {
  const { t } = useTranslation("DataView");
  const [textPending, startTransition] = useTransition();
  const [searchParams, setSearchParams] = useSearchParams();
  const headerRef = useRef<any>(null);
  const searchRef = useRef<HTMLDivElement | null>(null);
  const searchInput = useRef<HTMLInputElement | null>(null);
  const [searchExpanded, setSearchExpenaded] = useState(
    Boolean(searchInput.current?.value || "")
  );
  const breakpoint = useBreakpoint();
  const [filterBox, setFilterBox] = useState<boolean>(
    !props.disableFiltersOpenByDefault && breakpoint.md
  );

  const tableBreakpoint = breakpoint.lg;

  const sortField = useMemo(
    () => searchParams.get("sortField") || "",
    [searchParams]
  );
  const sortDirection = useMemo(
    () => searchParams.get("sortDirection") || SortDirection.None,
    [searchParams]
  );
  const page = useMemo(
    () => parseInt(searchParams.get("page") || "0"),
    [searchParams]
  );
  const rowsPerPage = useMemo(
    () => parseInt(searchParams.get("rowsPerPage") || "25"),
    [searchParams]
  );

  const [filterTemplate, setFilterTemplate] = useState<QueryFilterTemplate[]>(
    []
  );

  const filters = useMemo(
    () =>
      paramStringToObject(
        searchParams.get("filters") || filtersToParamString([])
      ),
    [searchParams]
  );

  const [isLoadable, setIsLoadable] = useState<boolean>(false);
  const [searched, setSearched] = useState(false);

  const loadQuery = async () => {
    const res = await props.queryFunction?.();
    if (res?.data?.data) {
      setFilterTemplate(res.data.data);
    }
    setIsLoadable(true);
  };

  const handleClickFilterButton = () => {
    setFilterBox((e) => !e);
  };

  const handleCloseFilters = () => {
    setFilterBox(false);
  };

  const handleSearch = (searchStr: string) => {
    startTransition(() => {
      setSearchParams(
        (sp) => {
          sp.set("search", searchStr);
          return sp;
        },
        { replace: searched }
      );
    });
  };

  useEffect(() => {
    loadQuery();
  }, []);

  useDidMountEffect(() => {
    if (props.loading) return;
    if (props.count < page * rowsPerPage) {
      setSearchParams((sp) => {
        sp.set("page", "0");
        return sp;
      });
    }
  }, [props.count]);

  const handleChange = () => {
    if (!isLoadable) {
      return;
    }
    if (filterTemplate.length > 0 && searchParams.size === 0) {
      for (const template of filterTemplate) {
        if (template.defaultSortDirection) {
          setSearchParams(
            (sp) => {
              sp.set("sortField", template.field);
              if (template.defaultSortDirection)
                sp.set("sortDirection", template.defaultSortDirection);
              return sp;
            },
            { replace: true }
          );
          return;
        }
      }
    }
    startTransition(async () => {
      await props.onChange?.({
        search: searchInput.current?.value || "",
        filters: prepareFilterString(filters),
        limit: rowsPerPage,
        skip: page * rowsPerPage,
        sortDirection: sortDirection as SortDirection,
        sortField,
      });
    });
  };

  const handleChangeFilters = (
    newFilters: Filter[],
    sort?: { field?: string; direction?: SortDirection }
  ) => {
    setSearchParams((sp) => {
      sp.set("filters", filtersToParamString(newFilters));
      sp.set("sortField", sort?.field || "");
      if (sort?.direction) sp.set("sortDirection", sort.direction);
      return sp;
    });
  };

  useRefresh(handleChange);

  useDidMountEffect(() => {
    const newSearch = searchParams.get("search") || "";
    if (searchInput.current) searchInput.current.value = newSearch;
    if (newSearch) setSearched(true);
    else setSearched(false);
    handleChange();
  }, [searchParams, isLoadable]);

  useEffect(() => {
    loadQuery();
  }, []);
  return (
    <Box
      display="flex"
      height={1}
      maxWidth={1}
      sx={{ overflowX: breakpoint.md ? "hidden" : undefined }}
    >
      <MainContainer
        open={filterBox}
        mobile={!breakpoint.md}
        sx={{
          flexGrow: 1,
          // transition: "margin 0.3s",
        }}
      >
        <Box
          ref={headerRef}
          component={Paper}
          sx={{
            p: 1.5,
            mb: 1,
            bgcolor: (theme) => theme.palette.background.default,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderRadius: (theme) => (theme.shape.borderRadius as number) / 8,
          }}
        >
          <InputBase
            ref={searchRef}
            inputRef={searchInput}
            sx={{
              borderRadius: (theme) => (theme.shape.borderRadius as number) / 8,
              bgcolor: (theme) =>
                searchExpanded ? theme.palette.background.paper : "none",
              px: 1,
              py: 1,
              mr: 1,
              flexGrow: 1,
              transition: "50ms ease-in-out",
            }}
            onFocus={() => setSearchExpenaded(true)}
            onBlur={() => {
              if (searchRef.current && !searchInput.current?.value)
                setSearchExpenaded(false);
            }}
            onChange={() => handleSearch(searchInput.current?.value || "")}
            placeholder={t("Search", { topic: props.name })}
            endAdornment={
              <IconButton size="small">
                <SearchIcon
                  onClick={() => handleSearch(searchInput.current?.value || "")}
                />
              </IconButton>
            }
          />

          <Box sx={{ display: "flex", alignItems: "center" }}>
            {Boolean(props.queryFunction) && (
              <Tooltip placement="top" title={t("Filters")}>
                <IconButton onClick={handleClickFilterButton}>
                  {filterBox ? (
                    <FilterAltOffIcon sx={{ color: "primary.main" }} />
                  ) : (
                    <FilterAltIcon sx={{ color: "primary.main" }} />
                  )}
                </IconButton>
              </Tooltip>
            )}
            {props.button && (breakpoint.md || !searchExpanded) && (
              <AddButton
                tooltip={t("Add")}
                onClick={props.button.onClick}
                boxProps={{ ml: 1 }}
              />
            )}
          </Box>
        </Box>
        {tableBreakpoint ? (
          <DataTable
            content={{ count: props.count, docs: props.items }}
            filterTemplate={filterTemplate}
            headers={props.headers}
            onRowClick={props.onItemClick}
            pagination={props.pagination}
            loading={props.loading}
          />
        ) : (
          <CardList
            count={props.count}
            items={props.items}
            renderItem={props.renderItem}
            loading={props.loading}
          />
        )}
      </MainContainer>
      {Boolean(props.queryFunction) && (
        <>
          {breakpoint.md ? (
            <Drawer
              sx={{
                width: drawerWidth,
                flexShrink: 0,
                "& .MuiDrawer-paper": {
                  width: drawerWidth,
                  position: "relative",
                  borderRadius: (theme) =>
                    (theme.shape.borderRadius as number) / 8,
                },
              }}
              variant="persistent"
              anchor="right"
              open={filterBox}
            >
              <FilterBox
                sortField={sortField}
                sortDirection={sortDirection as SortDirection}
                open={filterBox}
                onClose={handleCloseFilters}
                filterTemplate={filterTemplate}
                onChange={handleChangeFilters}
              />
            </Drawer>
          ) : (
            <SwipeableDrawer
              anchor="bottom"
              open={filterBox}
              onOpen={() => setFilterBox(true)}
              onClose={handleCloseFilters}
              PaperProps={{
                sx: {
                  borderTopLeftRadius: 12,
                  borderTopRightRadius: 12,
                  maxHeight: "70%",
                },
              }}
            >
              <FilterBox
                sortField={sortField}
                sortDirection={sortDirection as SortDirection}
                open={filterBox}
                onClose={handleCloseFilters}
                filterTemplate={filterTemplate}
                onChange={handleChangeFilters}
              />
            </SwipeableDrawer>
          )}
        </>
      )}
    </Box>
  );
}
